import { Ability } from '@casl/ability';
import {
  Actions,
  ResultStatus,
  Subjects,
  UpsertResource,
} from '@dispatch/shared/types';
import { ConfirmModal, SearchField, SectionWithMeta } from '@dispatch/web/ui';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import { useContext, useEffect } from 'react';
import { ResourceContext } from '../../ResourcesDetails';
import {
  AddAdditionalData,
  AdditionalDataKeyValuePair,
} from './AddAdditionalData';
import React from 'react';
import { Close } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { isEmpty } from 'lodash';

export type AdditionalDataProps = {
  permissions: Ability<[Actions, Subjects]> | undefined;
  upsertResource: UpsertResource;
  upsertResourceStatus?: ResultStatus;
  isReadOnly?: boolean;
};

export function AdditionalDataTab({
  permissions,
  upsertResource,
  upsertResourceStatus,
  isReadOnly = false,
}: AdditionalDataProps) {
  const { data } = useContext(ResourceContext);
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');

  const [cards, setCards] = React.useState<string[][]>();
  const [openModal, setOpenModal] = React.useState(false);
  const [cardData, setCardData] = React.useState<AdditionalDataKeyValuePair>();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [deleteCard, setDeleteCard] = React.useState<string[]>([]);

  useEffect(() => {
    const kvpObject = data?.metadata as Record<string, string>;
    if (kvpObject) {
      setCards(Object.keys(kvpObject).map((key) => [key, kvpObject[key]]));
    }
  }, [JSON.stringify(data?.metadata)]);

  useEffect(() => {
    if (upsertResourceStatus === 'success') {
      setOpenModal(false);
    }
  }, [upsertResourceStatus]);

  const saveData = (values: AdditionalDataKeyValuePair) => {
    const originalObject = data?.metadata;
    if (originalObject) {
      originalObject[values.key] = values.value;
    }
    const postBody = {
      ...data,
      metadata: originalObject as Record<string, string>,
    };
    upsertResource(postBody);
  };

  const onDeleteClick = (card: string[]) => {
    setDeleteCard(card);
    setShowConfirmModal(true);
  };

  const onConfirmDelete = () => {
    const originalObject = data?.metadata;
    if (originalObject && deleteCard) {
      delete originalObject[deleteCard[0]];
    }
    const postBody = {
      ...data,
      metadata: originalObject as Record<string, string>,
    };
    upsertResource(postBody);
    setShowConfirmModal(false);
  };

  const onDenyDelete = () => {
    setShowConfirmModal(false);
  };

  const onSearchByName = (value: string) => {
    const kvpObject = data?.metadata as Record<string, string>;
    if (kvpObject) {
      const cardsList = Object.keys(kvpObject).map((key) => [
        key,
        kvpObject[key],
      ]);
      const filteredCards = cardsList?.filter((x) =>
        x[0].toLowerCase().includes(value.trim().toLowerCase())
      );
      if (!isEmpty(value)) {
        setCards(filteredCards);
      } else {
        setCards(cardsList);
      }
    }
  };

  const renderConfirmModal = () => {
    return (
      <ConfirmModal
        onConfirm={onConfirmDelete}
        onDeny={onDenyDelete}
        confirmContent="Do you really want to delete?"
        confirmTitle="Delete Card"
        confirmButtonLabel="Delete"
        denyButtonLabel="Cancel"
        modalType={'delete'}
      />
    );
  };

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Box hidden={!hasUpsertPermissions}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            marginBottom: '10px',
          }}
        >
          <SearchField
            backgroundColor={grey[50]}
            searchIconColor={grey[900]}
            clearButtonColor={grey[900]}
            textColor={grey[900]}
            onSearch={(value: string) => onSearchByName(value)}
          />
          <Box hidden={!hasUpsertPermissions || isReadOnly}>
            <AddAdditionalData
              modalOpen={openModal && !isReadOnly}
              setModalOpen={setOpenModal}
              cardData={cardData}
              onSave={saveData}
              upsertResourceStatus={upsertResourceStatus}
            />
          </Box>
        </Stack>
        <SectionWithMeta title={'Additional Data'}>
          <Grid container alignItems="center" spacing={2}>
            {cards?.map((card) => (
              <Grid item xs={3}>
                <Card>
                  <CardHeader
                    action={
                      <IconButton
                        disabled={isReadOnly}
                        onClick={() => onDeleteClick(card)}
                      >
                        <Close
                          sx={{
                            fontSize: '15px',
                          }}
                        />
                      </IconButton>
                    }
                    sx={{
                      padding: '0 8px',
                    }}
                  />
                  <CardActionArea
                    onClick={() => {
                      setCardData({ key: card[0], value: card[1] });
                      setOpenModal(true);
                    }}
                  >
                    <CardContent
                      sx={{ height: '100%', padding: '0 8px 8px 8px' }}
                    >
                      <TextField
                        label={card[0]}
                        type="text"
                        value={card[1]}
                        inputProps={{
                          readOnly: true,
                        }}
                        variant="standard"
                      />
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </SectionWithMeta>
        {showConfirmModal && renderConfirmModal()}
      </Box>
    </div>
  );
}
